import { z } from "zod";
import { CALL_SESSION_STATUS, CALL_SESSION_TYPE } from "../constants/agent-consts";

// ============================================================================
// BASE CALL SESSION SCHEMA (matches backend DbCallSessionOutput)
// ============================================================================

// To sync with DbCallSessionOutput in backend/schemas/call-session.ts
const BaseCallSessionSchema = z.object({
  // Primary fields
  id: z.string(),

  // Relations
  followUpId: z.string(),
  phaseId: z.string(),

  // Scheduling
  scheduledAt: z.string(),
  sessionType: z.enum(CALL_SESSION_TYPE),
  status: z.enum(CALL_SESSION_STATUS),

  // Retell integration
  callAgentProvider: z.string().nullable().optional(),
  callAgentProviderId: z.string().nullable().optional(),

  // Attempts tracking
  maxAttempts: z.number(),
  attemptsCount: z.number(),

  // Priority and rescheduling
  priorityLevel: z.number(),
  rescheduledReason: z.string().nullable().optional(),
  rescheduledAt: z.string().nullable().optional(),

  // Completion
  completedAt: z.string().nullable().optional(),

  // Metadata
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  isVirtual: z.boolean().default(false),

  // Timestamps
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
});

export const CallSessionSchema = BaseCallSessionSchema;

export const CallSessionListItemSchema = BaseCallSessionSchema.extend({
  followUpStatus: z.string().nullable().optional(),
  patientName: z.string().nullable().optional(),
  phaseName: z.string().nullable().optional(),
});

export const CallSessionListSchema = z.array(CallSessionListItemSchema);

// ============================================================================
// CREATE/UPDATE PAYLOAD SCHEMAS (camelCase for API)
// ============================================================================

export const CreateCallSessionBodySchema = z.object({
  followUpId: z.string().uuid("Invalid follow-up ID"),
  phaseId: z.string().uuid("Invalid phase ID"),
  scheduledAt: z.string(),
  sessionType: z.enum(CALL_SESSION_TYPE),
  maxAttempts: z.number(),
  priorityLevel: z.number(),
});

export const UpdateCallSessionBodySchema = z.object({
  id: z.string().uuid("Invalid call session ID"),
  status: z.enum(CALL_SESSION_STATUS),
  attemptsCount: z.number(),
  rescheduledReason: z.string().nullable().optional(),
  rescheduledAt: z.string().nullable().optional(),
  completedAt: z.string().nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
});

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type CallSession = z.infer<typeof CallSessionSchema>;
export type CallSessionListItem = z.infer<typeof CallSessionListItemSchema>;
export type CreateCallSessionBody = z.infer<typeof CreateCallSessionBodySchema>;
export type UpdateCallSessionBody = z.infer<typeof UpdateCallSessionBodySchema>;
